import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Avatar } from '../components/Avatar';
import api from '../utils/api';
import { toast } from 'sonner';
import { formatDate } from '../utils/helpers';
import EditAmountModal from '../components/EditAmountModal';
import { ChevronLeft, ChevronRight, Calendar as CalIcon, MapPin, Users as UsersIcon, Plus, X, AlertTriangle, Send, Copy, Download, ClipboardCheck, ShieldCheck } from 'lucide-react';

const toISO = (d) => {
  const yr = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${yr}-${mo}-${da}`;
};

const parseISO = (s) => {
  if (!s) return new Date();
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const addDays = (isoDate, delta) => {
  const d = parseISO(isoDate);
  d.setDate(d.getDate() + delta);
  return toISO(d);
};

const statusConfig = {
  available: { label: 'Available', dot: 'bg-green-500', text: 'text-green-700 dark:text-green-400', badge: 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/30' },
  busy:      { label: 'Busy',      dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400', badge: 'bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/30' },
  overbooked:{ label: 'Overbooked',dot: 'bg-red-500',   text: 'text-red-700 dark:text-red-400',   badge: 'bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/30' },
  blocked:   { label: 'Blocked',   dot: 'bg-gray-600',  text: 'text-foreground',  badge: 'bg-secondary border-border' },
};

const DayBoard = () => {
  const todayISO = useMemo(() => toISO(new Date()), []);
  const [searchParams, setSearchParams] = useSearchParams();
  const [date, setDate] = useState(() => searchParams.get('date') || todayISO);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quickAssignFor, setQuickAssignFor] = useState(null); // event_id to add crew to
  const [editingAssignmentId, setEditingAssignmentId] = useState(null);
  const [amountEditFor, setAmountEditFor] = useState(null);
  const [reminderFor, setReminderFor] = useState(null); // event_id to send reminder for
  const [sendingReminder, setSendingReminder] = useState(false);
  const [savingCrewId, setSavingCrewId] = useState(null);
  const [pendingConflict, setPendingConflict] = useState(null);
  // pendingConflict = { event, crew, conflicts }

  // On first load, if no ?date= param, fetch next-active-date
  useEffect(() => {
    if (searchParams.get('date')) return;
    (async () => {
      try {
        const r = await api.get('/api/schedule/next-active-date');
        if (r?.data?.date) setDate(r.data.date);
      } catch (e) {
        console.error('next-active-date failed', e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL in sync with selected date (so browser back/forward + shareable links work)
  useEffect(() => {
    const current = searchParams.get('date');
    if (current !== date) {
      setSearchParams({ date }, { replace: true });
    }
  }, [date, searchParams, setSearchParams]);

  const fetchDay = useCallback(async (d) => {
    setLoading(true);
    try {
      const r = await api.get(`/api/schedule/day?date=${d}`);
      setData(r.data);
    } catch (e) {
      console.error('schedule/day failed', e);
      toast.error('Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchDay(date); }, [date, fetchDay]);

  const events = data?.events || [];
  const crew = data?.crew || [];
  const assignedCrewIdsForQuickAssign = useMemo(() => {
    if (!quickAssignFor) return new Set();
    const ev = events.find(e => e.event_id === quickAssignFor);
    return new Set((ev?.assignments || []).map(a => a.crew_member_id));
  }, [quickAssignFor, events]);

  const doAssign = async (event, crewMember, dayType, force = false) => {
    setSavingCrewId(`${crewMember.crew_id}_${dayType}`);
    try {
      if (!force) {
        const resp = await api.get(
          `/api/assignments/check-conflicts?event_id=${event.event_id}&crew_member_ids=${crewMember.crew_id}`
        );
        const row = (resp.data || [])[0];
        if (row && (row.blocked || (row.conflicts || []).length > 0)) {
          setPendingConflict({ event, crew: crewMember, row, dayType });
          setSavingCrewId(null);
          return;
        }
      }
      await api.post('/api/assignments', {
        event_id: event.event_id,
        crew_assignments: [{
          crew_member_id: crewMember.crew_id,
          day_type: dayType,
          currency: 'USD',
          custom_amount_usd: 0,
          custom_amount_inr: 0,
        }],
      });
      toast.success(`Assigned ${crewMember.full_name} (${dayType === 'half_day' ? 'Half Day' : 'Full Day'})`);
      setPendingConflict(null);
      setQuickAssignFor(null);
      await fetchDay(date);
    } catch (e) {
      console.error('assign failed', e);
      toast.error('Failed to assign');
    } finally {
      setSavingCrewId(null);
    }
  };

  const toggleDayType = async (assignment) => {
    const next = assignment.day_type === 'half_day' ? 'full_day' : 'half_day';
    try {
      await api.put(`/api/assignments/${assignment.assignment_id}/day-type`, { day_type: next });
      toast.success(`Changed to ${next === 'half_day' ? 'Half Day' : 'Full Day'}`);
      setEditingAssignmentId(null);
      await fetchDay(date);
    } catch (e) {
      console.error('toggle day type failed', e);
      toast.error('Failed to update');
    }
  };

  const changeRole = async (assignment, role) => {
    if (!role || role === (assignment.role_in_event || assignment.crew_member_role)) return;
    try {
      await api.put(`/api/assignments/${assignment.assignment_id}/role`, { role_in_event: role });
      toast.success('Role updated for this event');
      setEditingAssignmentId(null);
      await fetchDay(date);
    } catch (e) {
      console.error('change role failed', e);
      toast.error('Failed to update role');
    }
  };

  const removeAssignment = async (assignment) => {
    try {
      await api.delete(`/api/assignments/${assignment.assignment_id}`);
      toast.success(`Removed ${assignment.crew_member_name}`);
      setEditingAssignmentId(null);
      await fetchDay(date);
    } catch (e) {
      console.error('remove assignment failed', e);
      toast.error('Failed to remove');
    }
  };

  const sendReminder = async (event, type) => {
    if (!event.assignments || event.assignments.length === 0) return;
    setSendingReminder(true);
    try {
      const resp = await api.post('/api/notifications/send', {
        assignment_ids: event.assignments.map(a => a.assignment_id),
        notification_type: type, // 'email' | 'sms' | 'both'
      });
      const results = resp.data?.results || resp.data || [];
      const successCount = Array.isArray(results) ? results.filter(r => r.success || r.email_sent || r.sms_sent).length : event.assignments.length;
      toast.success(`Reminder sent to ${successCount} crew (${type === 'both' ? 'Email + SMS' : type.toUpperCase()})`);
      setReminderFor(null);
    } catch (e) {
      console.error('send reminder failed', e);
      toast.error('Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const sendGearChecklist = async (event) => {
    if (!event.assignments || event.assignments.length === 0) {
      toast.error('No accepted crew on this event');
      return;
    }
    setSendingReminder(true);
    try {
      const resp = await api.post(`/api/events/${event.event_id}/send-gear-checklist`);
      const { sent_count, total } = resp.data || {};
      toast.success(`Pre-event checklist sent to ${sent_count}/${total} crew`);
      fetchDay(date);
    } catch (e) {
      console.error('send gear checklist failed', e);
      toast.error('Failed to send checklist');
    } finally {
      setSendingReminder(false);
    }
  };

  const buildDayPlanText = () => {
    if (!data) return '';
    const heading = `📅 ${formatDate(date)}`;
    const evCount = events.length;
    if (evCount === 0) {
      return `${heading}\n\nNo events scheduled.\n`;
    }
    const lines = [
      heading,
      `${evCount} event${evCount !== 1 ? 's' : ''} scheduled`,
      '',
    ];
    events.forEach((ev, idx) => {
      const timeRange = (ev.start_time && ev.end_time) ? `${ev.start_time} – ${ev.end_time}` : 'TBD';
      const couple = ev.bride_name && ev.groom_name ? `${ev.bride_name} & ${ev.groom_name}` : (ev.project_name || '');
      lines.push(`${idx + 1}. ${timeRange} — ${ev.event_name}${ev.event_type ? ` (${ev.event_type})` : ''}`);
      if (couple) lines.push(`   ${couple}`);
      if (ev.location || ev.venue_address) {
        lines.push(`   📍 ${[ev.location, ev.venue_address].filter(Boolean).join(' · ')}`);
      }
      // Staffing
      const req = {
        photo: ev.required_photographers || 0,
        video: ev.required_videographers || 0,
        cine: ev.required_cinematographers || 0,
      };
      const totalReq = req.photo + req.video + req.cine;
      if (totalReq > 0) {
        const countRole = (pattern) => ev.assignments.filter(a => {
          const r = (a.role_in_event || a.crew_member_role || '').toLowerCase();
          if (pattern === 'photo') return r.includes('photograph');
          if (pattern === 'video') return r.includes('videograph');
          if (pattern === 'cine') return r.includes('cinematograph');
          return false;
        }).length;
        const got = { photo: countRole('photo'), video: countRole('video'), cine: countRole('cine') };
        const parts = [];
        if (req.photo > 0) parts.push(`Photo ${got.photo}/${req.photo}`);
        if (req.video > 0) parts.push(`Video ${got.video}/${req.video}`);
        if (req.cine > 0) parts.push(`Cine ${got.cine}/${req.cine}`);
        lines.push(`   Staffing: ${parts.join(' · ')}`);
      }
      // Crew
      if (ev.assignments.length === 0) {
        lines.push(`   ⚠️ No crew assigned`);
      } else {
        lines.push(`   Crew:`);
        ev.assignments.forEach(a => {
          const dt = a.day_type === 'half_day' ? 'Half' : 'Full';
          lines.push(`     • ${a.crew_member_name} (${a.role_in_event || a.crew_member_role}) — ${dt} Day`);
        });
      }
      lines.push('');
    });
    lines.push('— Ami Video Team');
    return lines.join('\n');
  };

  const copyDayPlan = async () => {
    const text = buildDayPlanText();
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Day plan copied to clipboard');
    } catch (e) {
      console.error('clipboard write failed', e);
      toast.error('Failed to copy');
    }
  };

  const downloadDayPlan = () => {
    const text = buildDayPlanText();
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `day-plan-${date}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Day plan downloaded');
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="day-board-page">
        {/* Header + Date picker */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-3">
            <CalIcon className="text-gold" size={24} />
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Day Schedule</h1>
          </div>
          <p className="text-sm text-muted-foreground">
            All events and crew availability for the selected day. Click + Add Crew on any event to assign quickly.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="day-board-date-controls">
          <button
            onClick={() => setDate(addDays(date, -1))}
            className="p-2.5 rounded-full border border-border hover:bg-secondary transition-colors"
            data-testid="prev-day-btn"
          >
            <ChevronLeft size={16} />
          </button>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="px-4 py-2.5 rounded-xl border border-border bg-card text-sm outline-none focus:ring-2 focus:ring-black/10"
            data-testid="day-board-date-input"
          />
          <button
            onClick={() => setDate(addDays(date, 1))}
            className="p-2.5 rounded-full border border-border hover:bg-secondary transition-colors"
            data-testid="next-day-btn"
          >
            <ChevronRight size={16} />
          </button>
          <button
            onClick={() => setDate(todayISO)}
            className="px-4 py-2.5 rounded-full border border-border text-xs font-medium hover:bg-secondary transition-colors"
            data-testid="today-btn"
          >
            Today
          </button>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={copyDayPlan}
              disabled={!data || events.length === 0}
              className="px-3 py-2 border border-black/15 text-foreground text-xs uppercase tracking-wide hover:bg-black hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              data-testid="copy-day-plan-btn"
              title="Copy day plan to clipboard"
            >
              <Copy size={14} /> Copy
            </button>
            <button
              onClick={downloadDayPlan}
              disabled={!data || events.length === 0}
              className="px-3 py-2 border border-border text-xs uppercase tracking-wide hover:border-gold hover:text-gold transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              data-testid="download-day-plan-btn"
              title="Download day plan as .txt"
            >
              <Download size={14} /> .txt
            </button>
          </div>
        </div>

        {/* Status summary below action row */}
        <div className="text-xs text-muted-foreground mb-6">
          {loading ? 'Loading…' : `${events.length} event${events.length !== 1 ? 's' : ''} · ${formatDate(date)}`}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Events column (2/3) */}
          <div className="lg:col-span-2 space-y-4" data-testid="events-column">
            <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Events</h2>
            {events.length === 0 && !loading && (
              <div className="border border-dashed border-border p-8 text-center text-sm text-muted-foreground" data-testid="no-events-empty">
                No events scheduled on this day.
              </div>
            )}
            {events.map(ev => (
              <div key={ev.event_id} className="rounded-2xl border border-border bg-card p-4 sm:p-5 shadow-[0_2px_10px_rgba(0,0,0,0.03)]" data-testid={`event-card-${ev.event_id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs uppercase tracking-wide bg-gold/10 text-gold px-2 py-0.5">
                        {ev.start_time || 'TBD'} – {ev.end_time || 'TBD'}
                      </span>
                      <span className="text-xs uppercase tracking-wide bg-muted px-2 py-0.5 text-muted-foreground">
                        {ev.event_type}
                      </span>
                    </div>
                    <h3 className="text-lg font-medium mt-2 truncate">{ev.event_name}</h3>
                    <p className="text-xs text-gold mt-0.5 truncate">
                      {ev.bride_name && ev.groom_name
                        ? `${ev.bride_name} & ${ev.groom_name}`
                        : ev.project_name}
                    </p>
                    {(ev.location || ev.venue_address) && (
                      <p className="text-xs text-muted-foreground mt-1 flex items-start gap-1.5">
                        <MapPin size={12} className="mt-0.5 flex-shrink-0" />
                        <span className="truncate">{[ev.location, ev.venue_address].filter(Boolean).join(' · ')}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Current assignments */}
                <div className="mt-3 pt-3 border-t border-border">
                  {(() => {
                    const req = {
                      photo: ev.required_photographers || 0,
                      video: ev.required_videographers || 0,
                      cine: ev.required_cinematographers || 0,
                    };
                    const totalReq = req.photo + req.video + req.cine;
                    if (totalReq === 0) return null;
                    const countRole = (pattern) =>
                      ev.assignments.filter(a => {
                        const r = (a.role_in_event || a.crew_member_role || '').toLowerCase();
                        if (pattern === 'photo') return r.includes('photograph');
                        if (pattern === 'video') return r.includes('videograph');
                        if (pattern === 'cine') return r.includes('cinematograph');
                        return false;
                      }).length;
                    const got = { photo: countRole('photo'), video: countRole('video'), cine: countRole('cine') };
                    const fullyStaffed = got.photo >= req.photo && got.video >= req.video && got.cine >= req.cine;
                    return (
                      <div className={`mb-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs ${fullyStaffed ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`} data-testid={`staffing-status-${ev.event_id}`}>
                        {req.photo > 0 && <span>Photo <strong>{got.photo}/{req.photo}</strong></span>}
                        {req.video > 0 && <span>Video <strong>{got.video}/{req.video}</strong></span>}
                        {req.cine > 0 && <span>Cine <strong>{got.cine}/{req.cine}</strong></span>}
                        <span className={`ml-auto px-2 py-0.5 ${fullyStaffed ? 'bg-green-100 dark:bg-green-500/15 border border-green-300' : 'bg-red-100 dark:bg-red-500/15 border border-red-300'}`}>
                          {fullyStaffed ? 'Fully staffed' : 'Under-staffed'}
                        </span>
                      </div>
                    );
                  })()}
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                      <UsersIcon size={12} />
                      Assigned ({ev.assignments.length})
                    </p>
                    <div className="flex items-center gap-3 relative">
                      {false && ev.assignments.length > 0 && (
                        <button
                          onClick={() => sendGearChecklist(ev)}
                          disabled={sendingReminder}
                          className="flex items-center gap-1 text-xs text-gold hover:text-gold/80 disabled:opacity-50"
                          data-testid={`send-checklist-btn-${ev.event_id}`}
                          title="Send pre-event gear checklist (Email + SMS)"
                        >
                          <ClipboardCheck size={12} /> Send Checklist
                        </button>
                      )}
                      {ev.assignments.length > 0 && (
                        <button
                          onClick={() => setReminderFor(reminderFor === ev.event_id ? null : ev.event_id)}
                          className="flex items-center gap-1 text-xs text-gold hover:text-gold/80"
                          data-testid={`send-reminder-btn-${ev.event_id}`}
                        >
                          <Send size={12} /> Send Reminder
                        </button>
                      )}
                      <button
                        onClick={() => setQuickAssignFor(ev.event_id)}
                        className="flex items-center gap-1 text-xs text-gold hover:text-gold/80"
                        data-testid={`add-crew-btn-${ev.event_id}`}
                      >
                        <Plus size={12} /> Add Crew
                      </button>
                      {reminderFor === ev.event_id && (
                        <div
                          className="absolute z-20 top-full right-0 mt-2 min-w-[180px] bg-background border border-border shadow-lg p-2 space-y-1"
                          data-testid={`reminder-popover-${ev.event_id}`}
                        >
                          <p className="text-[10px] uppercase tracking-wide text-muted-foreground px-2 pt-1">Notify {ev.assignments.length} crew via:</p>
                          <button
                            type="button"
                            disabled={sendingReminder}
                            onClick={() => sendReminder(ev, 'email')}
                            className="w-full text-left text-xs px-2 py-1.5 hover:bg-gold/10 hover:text-gold transition-colors disabled:opacity-50"
                            data-testid={`reminder-email-${ev.event_id}`}
                          >
                            📧 Email only
                          </button>
                          <button
                            type="button"
                            disabled={sendingReminder}
                            onClick={() => sendReminder(ev, 'sms')}
                            className="w-full text-left text-xs px-2 py-1.5 hover:bg-gold/10 hover:text-gold transition-colors disabled:opacity-50"
                            data-testid={`reminder-sms-${ev.event_id}`}
                          >
                            📱 SMS only
                          </button>
                          <button
                            type="button"
                            disabled={sendingReminder}
                            onClick={() => sendReminder(ev, 'both')}
                            className="w-full text-left text-xs px-2 py-1.5 hover:bg-gold/10 hover:text-gold transition-colors disabled:opacity-50"
                            data-testid={`reminder-both-${ev.event_id}`}
                          >
                            📧📱 Both
                          </button>
                          <button
                            type="button"
                            onClick={() => setReminderFor(null)}
                            className="w-full text-left text-xs px-2 py-1.5 text-muted-foreground hover:bg-muted transition-colors"
                          >
                            Cancel
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {ev.assignments.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No crew assigned yet</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {ev.assignments.map(a => (
                        <div key={a.assignment_id} className="relative">
                          <button
                            type="button"
                            onClick={() => setEditingAssignmentId(editingAssignmentId === a.assignment_id ? null : a.assignment_id)}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-muted/60 border border-border hover:border-gold text-xs transition-colors"
                            data-testid={`assignment-pill-${a.assignment_id}`}
                          >
                            <Avatar name={a.crew_member_name} size={18} />
                            {a.crew_member_name}
                            <span className="text-muted-foreground">· {a.role_in_event || a.crew_member_role}</span>
                            <span className="text-gold/90 font-medium">· {a.day_type === 'half_day' ? 'Half' : 'Full'}</span>
                            {false && a.gear_confirmation_status === 'confirmed' && (
                              <span
                                className="ml-1 inline-flex items-center gap-0.5 text-green-700 dark:text-green-400"
                                title="Gear confirmed"
                                data-testid={`gear-confirmed-${a.assignment_id}`}
                              >
                                <ShieldCheck size={12} />
                              </span>
                            )}
                            {false && a.gear_checklist_sent_at && a.gear_confirmation_status !== 'confirmed' && (
                              <span
                                className="ml-1 inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-400"
                                title="Checklist sent · awaiting confirmation"
                                data-testid={`gear-pending-${a.assignment_id}`}
                              >
                                <ClipboardCheck size={12} />
                              </span>
                            )}
                          </button>
                          {editingAssignmentId === a.assignment_id && (
                            <div
                              className="absolute z-20 top-full left-0 mt-1 min-w-[200px] bg-background border border-border shadow-lg p-2 space-y-1"
                              data-testid={`edit-assignment-popover-${a.assignment_id}`}
                            >
                              <div className="px-2 pt-1 pb-1.5 border-b border-border mb-1">
                                <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Role for this event</p>
                                <select
                                  value={a.role_in_event || a.crew_member_role || ''}
                                  onChange={(e) => changeRole(a, e.target.value)}
                                  className="w-full text-xs px-2 py-1.5 border border-border bg-background rounded outline-none focus:border-gold"
                                  data-testid={`assignment-role-select-${a.assignment_id}`}
                                >
                                  {Array.from(new Set([(a.role_in_event || a.crew_member_role || ''), 'Photographer', 'Videographer', 'Cinematographer'].filter(Boolean))).map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              </div>
                              <button
                                type="button"
                                onClick={() => toggleDayType(a)}
                                className="w-full text-left text-xs px-2 py-1.5 hover:bg-gold/10 hover:text-gold transition-colors"
                                data-testid={`toggle-day-type-${a.assignment_id}`}
                              >
                                Switch to {a.day_type === 'half_day' ? 'Full Day' : 'Half Day'}
                              </button>
                              <button
                                type="button"
                                onClick={() => { setAmountEditFor(a); setEditingAssignmentId(null); }}
                                className="w-full text-left text-xs px-2 py-1.5 hover:bg-gold/10 hover:text-gold transition-colors"
                                data-testid={`edit-amount-btn-${a.assignment_id}`}
                              >
                                Edit amount
                              </button>
                              <button
                                type="button"
                                onClick={() => removeAssignment(a)}
                                className="w-full text-left text-xs px-2 py-1.5 text-red-600 dark:text-red-400 hover:bg-red-500/10 transition-colors"
                                data-testid={`remove-assignment-${a.assignment_id}`}
                              >
                                Remove from event
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingAssignmentId(null)}
                                className="w-full text-left text-xs px-2 py-1.5 text-muted-foreground hover:bg-muted transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Inline quick-assign popover */}
                {quickAssignFor === ev.event_id && (
                  <div className="mt-3 border border-gold/40 bg-gold/5 p-3" data-testid={`quick-assign-panel-${ev.event_id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs uppercase tracking-wide text-gold">Select a crew member</p>
                      <button onClick={() => setQuickAssignFor(null)} className="text-muted-foreground hover:text-foreground">
                        <X size={14} />
                      </button>
                    </div>
                    <div className="space-y-1 max-h-64 overflow-y-auto">
                      {crew.map(c => {
                        const alreadyAssigned = assignedCrewIdsForQuickAssign.has(c.crew_id);
                        const cfg = statusConfig[c.status] || statusConfig.available;
                        const savingHalf = savingCrewId === `${c.crew_id}_half_day`;
                        const savingFull = savingCrewId === `${c.crew_id}_full_day`;
                        return (
                          <div
                            key={c.crew_id}
                            className={`flex flex-col sm:flex-row sm:items-center gap-2 px-2 py-2 border text-sm ${
                              alreadyAssigned
                                ? 'border-border bg-muted/30 text-muted-foreground'
                                : 'border-border hover:border-gold hover:bg-gold/5'
                            }`}
                            data-testid={`quick-assign-crew-row-${c.crew_id}`}
                          >
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                              <span className="truncate">{c.full_name}</span>
                              <span className="text-xs text-muted-foreground truncate">· {c.role}</span>
                              <span className={`text-xs ${cfg.text} flex-shrink-0 ml-auto sm:ml-0`}>
                                {alreadyAssigned ? 'Assigned' : cfg.label}
                              </span>
                            </div>
                            {!alreadyAssigned && (
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <button
                                  type="button"
                                  disabled={savingHalf || savingFull}
                                  onClick={() => doAssign(ev, c, 'half_day')}
                                  className="px-2.5 py-1 text-xs border border-border hover:border-gold hover:text-gold transition-colors disabled:opacity-50"
                                  data-testid={`assign-half-day-${c.crew_id}`}
                                >
                                  {savingHalf ? '…' : 'Half Day'}
                                </button>
                                <button
                                  type="button"
                                  disabled={savingHalf || savingFull}
                                  onClick={() => doAssign(ev, c, 'full_day')}
                                  className="px-2.5 py-1 text-xs bg-primary text-primary-foreground hover:bg-black/90 transition-colors disabled:opacity-50 font-medium"
                                  data-testid={`assign-full-day-${c.crew_id}`}
                                >
                                  {savingFull ? '…' : 'Full Day'}
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Crew sidebar (1/3) */}
          <div className="space-y-3" data-testid="crew-sidebar">
            <h2 className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Crew availability</h2>
            {crew.map(c => {
              const cfg = statusConfig[c.status] || statusConfig.available;
              return (
                <div
                  key={c.crew_id}
                  className={`rounded-2xl border p-3 ${cfg.badge}`}
                  data-testid={`crew-card-${c.crew_id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-2 h-2 rounded-full ${cfg.dot}`} />
                    <span className="font-medium text-sm truncate">{c.full_name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground">{c.role}</p>
                  <p className={`text-xs mt-1 font-medium ${cfg.text}`}>
                    {cfg.label}
                    {c.status === 'busy' || c.status === 'overbooked'
                      ? ` · ${c.conflicts.length} event${c.conflicts.length !== 1 ? 's' : ''}`
                      : ''}
                  </p>
                  {c.conflicts && c.conflicts.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5 text-[11px] text-muted-foreground">
                      {c.conflicts.map(cf => (
                        <li key={cf.event_id} className="truncate">
                          • {cf.start_time}–{cf.end_time} {cf.event_name}
                        </li>
                      ))}
                    </ul>
                  )}
                  {c.status === 'blocked' && c.blocked_note && (
                    <p className="text-[11px] text-muted-foreground mt-1 italic">Note: {c.blocked_note}</p>
                  )}
                </div>
              );
            })}
            {crew.length === 0 && !loading && (
              <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                No crew members yet.
              </div>
            )}
          </div>
        </div>

        {/* Conflict confirmation modal */}
        {pendingConflict && (
          <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" data-testid="conflict-confirm-modal">
            <div className="bg-background w-full max-w-lg max-h-[85vh] overflow-y-auto">
              <div className="p-5 border-b border-border flex items-center gap-2">
                <AlertTriangle className="text-amber-500" size={20} />
                <h3 className="text-lg font-medium">Scheduling Conflict</h3>
              </div>
              <div className="p-5">
                <p className="text-sm mb-3">
                  <span className="font-medium">{pendingConflict.crew.full_name}</span> is {pendingConflict.row.blocked ? 'blocked' : 'already assigned'} on this day.
                </p>
                {pendingConflict.row.blocked ? (
                  <p className="text-xs text-red-600 dark:text-red-400">⛔ BLOCKED{pendingConflict.row.blocked_note ? ` — ${pendingConflict.row.blocked_note}` : ''}</p>
                ) : (
                  <ul className="space-y-1 text-xs pl-4">
                    {pendingConflict.row.conflicts.map(cf => (
                      <li key={cf.event_id} className={cf.time_overlap ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}>
                        • {cf.event_name}
                        {cf.start_time && cf.end_time ? ` (${cf.start_time}–${cf.end_time})` : ''}
                        {cf.time_overlap && <span className="ml-1 font-semibold">— TIME OVERLAP</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="p-5 border-t border-border flex gap-3">
                <button
                  onClick={() => setPendingConflict(null)}
                  className="flex-1 border border-border px-4 py-2.5 text-sm hover:bg-accent"
                  data-testid="conflict-go-back-btn"
                >
                  GO BACK
                </button>
                <button
                  onClick={() => doAssign(pendingConflict.event, pendingConflict.crew, pendingConflict.dayType || 'full_day', true)}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2.5 text-sm font-medium"
                  data-testid="conflict-assign-anyway-btn"
                >
                  ASSIGN ANYWAY ({pendingConflict.dayType === 'half_day' ? 'Half Day' : 'Full Day'})
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {amountEditFor && (
        <EditAmountModal
          assignment={amountEditFor}
          onClose={() => setAmountEditFor(null)}
          onSaved={() => fetchDay(date)}
        />
      )}
    </Layout>
  );
};

export default DayBoard;
